/**
 * Results Pop-Out Panel Content
 * 
 * Access outputs and evaluations.
 * Per spec:
 * - Latest result summary
 * - Previous runs
 * - Comparison tools
 * - Export / Report actions
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faFileExport,
    faBalanceScale,
    faHistory,
    faFolder,
    faCheckCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

interface ResultsPopOutProps {
    onClose: () => void;
}

export const ResultsPopOut: React.FC<ResultsPopOutProps> = ({ onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we're in a project context
    const projectMatch = location.pathname.match(/\/app\/lab\/project\/([^/]+)/);
    const projectId = projectMatch ? projectMatch[1] : null;

    const handleNavigate = (path: string) => {
        navigate(path);
        onClose();
    };

    if (!projectId) {
        return (
            <div className="p-4">
                <div className="text-center py-8">
                    <FontAwesomeIcon icon={faFolder} className="h-8 w-8 text-gray-600 mb-3" />
                    <p className="text-sm text-gray-400 mb-2">No project open</p>
                    <p className="text-xs text-gray-500">
                        Open a project to view results
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-6">
            {/* Latest Result Summary */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faChartLine} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Latest Result</h3>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                    <p className="text-sm text-gray-400 text-center py-2">
                        No results yet
                    </p>
                </div>
            </div>

            {/* Result Views */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faHistory} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Views</h3>
                </div>
                <div className="space-y-1">
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}/results`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left group"
                    >
                        <FontAwesomeIcon icon={faChartLine} className="h-4 w-4 text-cyan-500/70 group-hover:text-cyan-400" />
                        <span className="text-sm text-gray-300 group-hover:text-white">All Results</span>
                    </button>
                    <button
                        onClick={() => handleNavigate(`/app/lab/project/${projectId}/compare`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-left group"
                    >
                        <FontAwesomeIcon icon={faBalanceScale} className="h-4 w-4 text-amber-500/70 group-hover:text-amber-400" />
                        <span className="text-sm text-gray-300 group-hover:text-white">Compare Results</span>
                    </button>
                </div>
            </div>

            {/* Validation Status */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faCheckCircle} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Validation</h3>
                </div>
                <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="h-3.5 w-3.5" />
                        <span>No WFA validation yet</span>
                    </div>
                </div>
            </div>

            {/* Export Actions */}
            <div>
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faFileExport} className="h-3.5 w-3.5 text-white" />
                    <h3 className="text-xs font-medium text-white uppercase tracking-wider">Export</h3>
                </div>
                <button
                    onClick={() => handleNavigate(`/app/lab/project/${projectId}/report`)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:border-cyan-500 hover:text-cyan-400 transition-colors"
                >
                    <FontAwesomeIcon icon={faFileExport} className="h-4 w-4" />
                    Generate Report
                </button>
            </div>
        </div>
    );
};

export default ResultsPopOut;
